import React from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import debounce from 'lodash/debounce';

import { AsyncSelectField, Input } from '../containers/Form';
import { closeModal } from '../actions/modal';
import { notificationActions } from '../components/Notification/redux/actions';
import Wizard, { getTooltipFromValidationErrors } from '../containers/Modal/Wizard';
import Strings from '../Strings';
import actions from '../pages/SuperUser/SuperuserHealthCenter/redux/actions';
import {
  CREATE_SUPERUSER_HEALTH_ALERT_SUBSCRIPTION,
  SEARCH_SUPERUSER_HEALTH_ALERT_SUBSCRIPTION_USERS,
  UPDATE_SUPERUSER_HEALTH_ALERT_SUBSCRIPTION,
} from '../pages/SuperUser/SuperuserHealthCenter/redux/constants';

export const EDIT_SUBSCRIPTION_MODAL_NAME = 'edit-subscription-modal';

export const formatAlertSubscriptionUserOption = user => {
  const fullName = [user.first_name, user.last_name]
    .filter(Boolean)
    .join(' ')
    .trim();
  const primaryLabel = fullName || user.username || user.email || Strings.healthMonitorAlerts.subscription.unnamedUser;
  const details = [
    user.username && user.username !== primaryLabel ? `@${user.username}` : '',
    user.organization_name || '',
  ].filter(Boolean);

  return {
    value: user.id,
    label: details.length > 0 ? `${primaryLabel} (${details.join(', ')})` : primaryLabel,
  };
};

export const getAlertSubscriptionUserOptions = response =>
  (response?.data || []).map(formatAlertSubscriptionUserOption);

function FieldHelp({ label, content }) {
  return (
    <div className="right-top-text">
      <span
        className="icon"
        data-tooltip-content={content}
        data-tooltip-id="tooltip"
        data-tooltip-place="right"
        aria-label={Strings.formatString(Strings.healthMonitorAlerts.fieldHelp, label)}
      >
        ?
      </span>
    </div>
  );
}

FieldHelp.propTypes = {
  label: PropTypes.string.isRequired,
  content: PropTypes.string.isRequired,
};

function EditSubscriptionModal(props) {
  const {
    data,
    closeModalWithNextAction,
    createSubscription,
    searchSubscriptionUsers,
    updateSubscription,
    showNotification,
  } = props;

  const isEdit = !!data?.subscription;
  const subscription = data?.subscription;
  const ruleId = data?.ruleId;

  const [linkedUser, setLinkedUser] = React.useState(
    subscription?.user_id
      ? {
          value: subscription.user_id,
          label:
            subscription.resolved_email || subscription.name || Strings.healthMonitorAlerts.subscription.recipientUser,
        }
      : null,
  );
  const [name, setName] = React.useState(subscription?.name || '');
  const [phoneNumberOverride, setPhoneNumberOverride] = React.useState(subscription?.phone_number_override || '');
  const [emailOverride, setEmailOverride] = React.useState(subscription?.email_override || '');
  const [smsEnabled, setSmsEnabled] = React.useState(subscription?.sms_enabled ?? true);
  const [emailEnabled, setEmailEnabled] = React.useState(subscription?.email_enabled ?? false);
  const [active, setActive] = React.useState(subscription?.active ?? true);
  const [userSearchFailed, setUserSearchFailed] = React.useState(false);

  const [validationErrors, setValidationErrors] = React.useState([]);
  const [highlightInvalidFields, setHighlightInvalidFields] = React.useState(false);
  const [isSaving, setIsSaving] = React.useState(false);

  const normalizedEmailOverride = emailOverride.trim();
  const normalizedPhoneNumberOverride = phoneNumberOverride.trim();
  const hasContactMethod =
    Boolean(linkedUser) || Boolean(normalizedPhoneNumberOverride) || Boolean(normalizedEmailOverride);
  const isContactMethodMissing = !hasContactMethod;
  const isActiveChannelMissing = active && !smsEnabled && !emailEnabled;

  // Validation effect
  React.useEffect(() => {
    const errors = [];

    // At least one contact method must be provided
    if (!hasContactMethod) {
      errors.push({
        property: 'contactMethod',
        propertyName: Strings.healthMonitorAlerts.subscription.contactMethod,
        missing: true,
      });
    }

    if (isActiveChannelMissing) {
      errors.push({
        property: 'notificationChannel',
        propertyName: Strings.healthMonitorAlerts.subscription.notificationChannel,
        missing: true,
      });
    }

    // Validate email format if provided
    if (normalizedEmailOverride && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(normalizedEmailOverride)) {
      errors.push({
        property: 'emailOverride',
        propertyName: Strings.healthMonitorAlerts.subscription.emailOverride,
        errors: [Strings.healthMonitorAlerts.subscription.invalidEmail],
        missing: false,
      });
    }

    // Validate phone format if provided (simple regex for international format)
    if (normalizedPhoneNumberOverride && !/^\+?[1-9]\d{1,14}$/.test(normalizedPhoneNumberOverride)) {
      errors.push({
        property: 'phoneNumberOverride',
        propertyName: Strings.healthMonitorAlerts.subscription.phoneNumberOverride,
        errors: [Strings.healthMonitorAlerts.subscription.invalidPhone],
        missing: false,
      });
    }

    setValidationErrors(errors);
  }, [hasContactMethod, isActiveChannelMissing, normalizedEmailOverride, normalizedPhoneNumberOverride]);

  const loadUserOptions = React.useMemo(
    () =>
      debounce((query, callback) => {
        const normalizedQuery = query.trim();
        if (normalizedQuery.length < 2) {
          setUserSearchFailed(false);
          callback([]);
          return;
        }

        searchSubscriptionUsers(normalizedQuery)
          .then(resp => {
            if (resp?.type !== `${SEARCH_SUPERUSER_HEALTH_ALERT_SUBSCRIPTION_USERS}/result`) {
              setUserSearchFailed(true);
              callback([]);
              return;
            }
            setUserSearchFailed(false);
            callback(getAlertSubscriptionUserOptions(resp?.response));
          })
          .catch(() => {
            setUserSearchFailed(true);
            callback([]);
          });
      }, 300),
    [searchSubscriptionUsers],
  );

  React.useEffect(() => () => loadUserOptions.cancel(), [loadUserOptions]);

  // Submit handler
  const onSubmit = () => {
    setIsSaving(true);

    const subscriptionData = {
      user_id: linkedUser?.value,
      name: name.trim() || undefined,
      phone_number_override: phoneNumberOverride.trim() || undefined,
      email_override: emailOverride.trim() || undefined,
      sms_enabled: smsEnabled,
      email_enabled: emailEnabled,
      active,
    };

    const action = isEdit
      ? updateSubscription(subscription.id, subscriptionData)
      : createSubscription(ruleId, subscriptionData);

    action.then(resp => {
      const expectedType = isEdit
        ? UPDATE_SUPERUSER_HEALTH_ALERT_SUBSCRIPTION
        : CREATE_SUPERUSER_HEALTH_ALERT_SUBSCRIPTION;

      if (resp?.type !== `${expectedType}/result`) {
        showNotification(
          resp?.response?.data?.error?.message || Strings.healthMonitorAlerts.subscription.saveFailed,
          5000,
          true,
        );
        setIsSaving(false);
        return;
      }

      showNotification(
        isEdit ? Strings.healthMonitorAlerts.subscription.updated : Strings.healthMonitorAlerts.subscription.created,
      );
      closeModalWithNextAction();
    });
  };

  const pages = [
    {
      id: 'edit-subscription-page',
      title: isEdit ? Strings.healthMonitorAlerts.subscription.edit : Strings.healthMonitorAlerts.subscription.create,
      content: (
        <>
          <p>{Strings.healthMonitorAlerts.subscription.description}</p>

          <div className="control-with-top-right-text">
            <AsyncSelectField
              id="linkedUser"
              label={Strings.healthMonitorAlerts.subscription.recipientUserOptional}
              value={linkedUser}
              onChange={setLinkedUser}
              loadOptions={loadUserOptions}
              placeholder={Strings.healthMonitorAlerts.subscription.searchPlaceholder}
              noOptionsMessage={({ inputValue }) => (
                inputValue.trim().length < 2
                  ? Strings.healthMonitorAlerts.subscription.enterAtLeast2Characters
                  : userSearchFailed
                    ? Strings.healthMonitorAlerts.subscription.searchFailed
                    : Strings.healthMonitorAlerts.subscription.noUsersFound
              )}
              isClearable
              cacheOptions={false}
            />
            <FieldHelp
              label={Strings.healthMonitorAlerts.subscription.recipientUser}
              content={Strings.healthMonitorAlerts.subscription.recipientUserHelp}
            />
          </div>
          <div className="control-with-top-right-text">
            <Input
              id="name"
              label={Strings.healthMonitorAlerts.subscription.labelOptional}
              value={name}
              onChange={e => setName(e.target.value)}
            />
            <FieldHelp
              label={Strings.healthMonitorAlerts.subscription.label}
              content={Strings.healthMonitorAlerts.subscription.labelHelp}
            />
          </div>

          <div className="control-with-top-right-text">
            <Input
              id="emailOverride"
              label={Strings.healthMonitorAlerts.subscription.emailOverrideOptional}
              value={emailOverride}
              onChange={e => setEmailOverride(e.target.value)}
              highlightInvalid={highlightInvalidFields}
              errorsForTooltip={validationErrors.find(e => e.property === 'emailOverride')?.errors || []}
            />
            <FieldHelp
              label={Strings.healthMonitorAlerts.subscription.emailOverride}
              content={Strings.healthMonitorAlerts.subscription.emailOverrideHelp}
            />
          </div>
          <div className="control-with-top-right-text">
            <Input
              id="phoneNumberOverride"
              label={Strings.healthMonitorAlerts.subscription.phoneNumberOverrideOptional}
              value={phoneNumberOverride}
              onChange={e => setPhoneNumberOverride(e.target.value)}
              highlightInvalid={highlightInvalidFields}
              errorsForTooltip={validationErrors.find(e => e.property === 'phoneNumberOverride')?.errors || []}
            />
            <FieldHelp
              label={Strings.healthMonitorAlerts.subscription.phoneNumberOverride}
              content={Strings.healthMonitorAlerts.subscription.phoneNumberOverrideHelp}
            />
          </div>

          <Input
            id="smsEnabled"
            label={Strings.healthMonitorAlerts.subscription.smsEnabled}
            type="checkbox"
            checked={smsEnabled}
            onChange={e => setSmsEnabled(e.target.checked)}
            rowClassName="checkbox-first"
          />
          <Input
            id="emailEnabled"
            label={Strings.healthMonitorAlerts.subscription.emailEnabled}
            type="checkbox"
            checked={emailEnabled}
            onChange={e => setEmailEnabled(e.target.checked)}
            rowClassName="checkbox-first"
          />
          <Input
            id="active"
            label={Strings.healthMonitorAlerts.active}
            type="checkbox"
            checked={active}
            onChange={e => setActive(e.target.checked)}
            rowClassName="checkbox-first"
          />

          {highlightInvalidFields && isContactMethodMissing && (
            <div className="warning-red-text">{Strings.healthMonitorAlerts.subscription.contactMethodRequired}</div>
          )}
          {highlightInvalidFields && isActiveChannelMissing && (
            <div className="warning-red-text">
              {Strings.healthMonitorAlerts.subscription.notificationChannelRequired}
            </div>
          )}
        </>
      ),
      nextButton: {
        text: isEdit
          ? Strings.healthMonitorAlerts.subscription.update
          : Strings.healthMonitorAlerts.subscription.create,
      },
      emptyFieldsCount: validationErrors.filter(e => e.missing).length,
      canGoNext: !isSaving && validationErrors.length === 0,
    },
  ];

  return (
    <Wizard
      name={EDIT_SUBSCRIPTION_MODAL_NAME}
      pages={pages}
      onSubmit={onSubmit}
      onNextButtonHover={setHighlightInvalidFields}
      nextButtonTooltip={getTooltipFromValidationErrors(validationErrors)}
    />
  );
}

EditSubscriptionModal.propTypes = {
  data: PropTypes.shape({
    subscription: PropTypes.object,
    ruleId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  }),
  onCancel: PropTypes.func,
  closeModalWithNextAction: PropTypes.func.isRequired,
  createSubscription: PropTypes.func,
  searchSubscriptionUsers: PropTypes.func.isRequired,
  updateSubscription: PropTypes.func,
  showNotification: PropTypes.func,
};

const mapDispatchToProps = (dispatch, ownProps) => ({
  createSubscription: (ruleId, data) => dispatch(actions.createSubscription(ruleId, data)),
  searchSubscriptionUsers: search => dispatch(actions.searchSubscriptionUsers(search)),
  updateSubscription: (subscriptionId, data) => dispatch(actions.updateSubscription(subscriptionId, data)),
  showNotification: (message, timeout, error) => dispatch(notificationActions.show(message, timeout, error)),
  closeModalWithNextAction: () => {
    dispatch(closeModal(EDIT_SUBSCRIPTION_MODAL_NAME));
    if (ownProps.data?.onSuccess) ownProps.data.onSuccess();
  },
});

export default connect(null, mapDispatchToProps)(EditSubscriptionModal);
